import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { adminService } from '../../services/api';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Modal from '../../components/common/Modal';
import PlacementHistory from '../../components/admin/PlacementHistory';
import {
  formatPlacementDate,
  getPlacements,
  isCrmSynced,
  placementIsCurrent,
  placementIsPlanned,
} from '../../utils/adminV2';
import {
  loadAssignmentPolicy,
  localActionsAllowed,
  crmEmployeeHref,
  crmEmployeesHref,
  MANAGE_IN_CRM_LABEL,
} from '../../utils/assignmentPolicy';
import {
  FiSearch,
  FiUserPlus,
  FiUserX,
  FiRepeat,
  FiUsers,
  FiChevronRight,
  FiExternalLink
} from 'react-icons/fi';

// Styled components (match the existing admin pages)
const PageContainer = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing[6]};
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const PageTitle = styled.h1`
  font-size: ${({ theme }) => theme.typography.fontSize.xl};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  margin: 0;
`;

const AlertMessage = styled.div`
  padding: ${({ theme }) => theme.spacing[3]};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};

  ${({ type, theme }) => {
    if (type === 'success') {
      return `
        background-color: ${theme.colors.success}15;
        color: ${theme.colors.success};
      `;
    } else if (type === 'error') {
      return `
        background-color: ${theme.colors.error}15;
        color: ${theme.colors.error};
      `;
    }
    return '';
  }}
`;

const HelperText = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0 0 ${({ theme }) => theme.spacing[3]};
`;

const SearchBox = styled.div`
  position: relative;
  width: 100%;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[8]};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primary}20;
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: ${({ theme }) => theme.spacing[2]};
  top: 50%;
  transform: translateY(-50%);
  color: ${({ theme }) => theme.colors.textSecondary};
  display: flex;
`;

const ResultList = styled.div`
  margin-top: ${({ theme }) => theme.spacing[2]};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  max-height: 340px;
  overflow-y: auto;
`;

const ResultItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => theme.spacing[3]};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: ${({ theme }) => theme.colors.backgroundLight};
  }
`;

const ClickableItem = styled(ResultItem)`
  cursor: pointer;
`;

const Person = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const PersonName = styled.span`
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PersonMeta = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RowActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[2]};
  flex-shrink: 0;
`;

const SmallButton = styled(Button)`
  padding: ${({ theme }) => theme.spacing[1]} ${({ theme }) => theme.spacing[2]};
  min-height: 34px;
  font-size: ${({ theme }) => theme.typography.fontSize.xs};

  svg {
    width: 14px;
    height: 14px;
  }
`;

// The shared Button has no destructive variant, so give Remove an explicit red.
const DangerButton = styled(SmallButton)`
  background-color: ${({ theme }) => theme.colors.error};
  color: ${({ theme }) => theme.colors.white};
  border: none;

  &:hover {
    background-color: ${({ theme }) => theme.colors.error};
    opacity: 0.9;
  }
`;

const SelectedBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => theme.spacing[3]};
  background-color: ${({ theme }) => theme.colors.backgroundLight};
  border-radius: ${({ theme }) => theme.borders.radius.lg};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const SelectedInfo = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const SelectedLabel = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const SelectedName = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semiBold};
  color: ${({ theme }) => theme.colors.text};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing[4]};

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const SectionTitle = styled.h3`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  font-size: ${({ theme }) => theme.typography.fontSize.md};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semiBold};
  margin: 0 0 ${({ theme }) => theme.spacing[2]};

  svg {
    width: 18px;
    height: 18px;
    color: ${({ theme }) => theme.colors.textSecondary};
  }
`;

const CountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 9999px;
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  background-color: ${({ theme }) => theme.colors.primary}15;
  color: ${({ theme }) => theme.colors.primary};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing[6]};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const MutedNote = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  padding: ${({ theme }) => theme.spacing[3]} 0;
  margin: 0;
`;

const SemanticsNote = styled.p`
  padding: ${({ theme }) => theme.spacing[3]};
  margin: 0 0 ${({ theme }) => theme.spacing[4]};
  border-left: 3px solid ${({ theme }) => theme.colors.info};
  background: ${({ theme }) => theme.colors.backgroundLight};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const SyncBadge = styled.span`
  display: inline-flex;
  margin-left: ${({ theme }) => theme.spacing[1]};
  padding: 1px 6px;
  border-radius: 999px;
  background: ${({ theme }) => `${theme.colors.info}15`};
  color: ${({ theme }) => theme.colors.info};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
`;

// Placements are managed in CRM (1.70.0): the local actions are replaced by
// one link per row to the CRM employee page, or to the CRM employees list.
const CrmLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[1]};
  padding: ${({ theme }) => theme.spacing[1]} ${({ theme }) => theme.spacing[2]};
  min-height: 34px;
  border: 1px solid ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  color: ${({ theme }) => theme.colors.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  text-decoration: none;
  white-space: nowrap;

  svg {
    width: 14px;
    height: 14px;
  }
`;

const ManageInCrm = ({ href }) => (href
  ? <CrmLink href={href} target="_blank" rel="noopener noreferrer">{MANAGE_IN_CRM_LABEL} <FiExternalLink /></CrmLink>
  : <MutedNote style={{ padding: 0 }}>{MANAGE_IN_CRM_LABEL} (CRM address not configured)</MutedNote>);

const PAGE_SIZE = 20;
const CURRENT_LIMIT = 200;

const fullName = (person) => `${person.firstName || ''} ${person.lastName || ''}`.trim();

const employeeMeta = (employee) => {
  const parts = [];
  if (employee.email) parts.push(employee.email);
  if (employee.department) parts.push(employee.department);
  return parts.join(' · ');
};

const currentPlacementAtCustomer = (employee, customerId) => getPlacements(employee)
  .find((placement) => Number(placement.customerId) === Number(customerId) && placementIsCurrent(placement));

const Assignments = () => {
  const [alert, setAlert] = useState(null);
  // null while loading: the page is read-only until the server says otherwise.
  const [policy, setPolicy] = useState(null);
  const localActions = localActionsAllowed(policy);

  useEffect(() => {
    let active = true;
    loadAssignmentPolicy().then((loaded) => { if (active) setPolicy(loaded); });
    return () => { active = false; };
  }, []);

  // Customer picker
  const [customerQuery, setCustomerQuery] = useState('');
  const [customerResults, setCustomerResults] = useState([]);
  const [customerSearching, setCustomerSearching] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // Current employees for the selected customer
  const [currentEmployees, setCurrentEmployees] = useState([]);
  const [currentLoading, setCurrentLoading] = useState(false);

  // Add-employee search
  const [addQuery, setAddQuery] = useState('');
  const [addResults, setAddResults] = useState([]);
  const [addSearching, setAddSearching] = useState(false);
  const [busyId, setBusyId] = useState(null);

  // Move flow
  const [moveTarget, setMoveTarget] = useState(null); // employee being moved
  const [moveQuery, setMoveQuery] = useState('');
  const [moveResults, setMoveResults] = useState([]);
  const [moveSearching, setMoveSearching] = useState(false);
  const [moveSubmitting, setMoveSubmitting] = useState(false);

  // Debounced customer search
  useEffect(() => {
    const term = customerQuery.trim();
    if (!term) {
      setCustomerResults([]);
      setCustomerSearching(false);
      return;
    }
    setCustomerSearching(true);
    const handle = setTimeout(async () => {
      try {
        const data = await adminService.getCustomers({ search: term, limit: PAGE_SIZE, page: 1 });
        setCustomerResults(data.customers || []);
      } catch (error) {
        console.error('Error searching customers:', error);
        setAlert({ type: 'error', message: error.message || 'Failed to search customers.' });
      } finally {
        setCustomerSearching(false);
      }
    }, 300);
    return () => clearTimeout(handle);
  }, [customerQuery]);

  const fetchCurrentEmployees = useCallback(async (customerId) => {
    setCurrentLoading(true);
    try {
      const data = await adminService.getEmployees({ customerId, limit: CURRENT_LIMIT, page: 1 });
      const employees = data.employees || [];
      setCurrentEmployees(employees.filter((employee) => {
        const atCustomer = getPlacements(employee)
          .filter((placement) => Number(placement.customerId) === Number(customerId));
        // Preserve legacy behavior until placements are available; once they
        // are, an ended historical placement must not appear as current — but a
        // planned (future-start) one must, so it keeps its Reassign/End
        // controls before day one.
        return atCustomer.length === 0
          || atCustomer.some((placement) => placementIsCurrent(placement) || placementIsPlanned(placement));
      }));
    } catch (error) {
      console.error('Error loading assigned employees:', error);
      setAlert({ type: 'error', message: error.message || 'Failed to load assigned employees.' });
    } finally {
      setCurrentLoading(false);
    }
  }, []);

  const refreshPlacementViews = useCallback(async (customerId) => {
    await Promise.all([
      fetchCurrentEmployees(customerId),
      adminService.getCustomerById(customerId)
        .then((details) => { if (details) setSelectedCustomer(details); })
        .catch(() => {}),
    ]);
  }, [fetchCurrentEmployees]);

  // Debounced add-employee search (only while a customer is selected)
  useEffect(() => {
    if (!selectedCustomer) return undefined;
    const term = addQuery.trim();
    if (!term) {
      setAddResults([]);
      setAddSearching(false);
      return undefined;
    }
    setAddSearching(true);
    const handle = setTimeout(async () => {
      try {
        const data = await adminService.getEmployees({ search: term, limit: PAGE_SIZE, page: 1 });
        setAddResults(data.employees || []);
      } catch (error) {
        console.error('Error searching employees:', error);
        setAlert({ type: 'error', message: error.message || 'Failed to search employees.' });
      } finally {
        setAddSearching(false);
      }
    }, 300);
    return () => clearTimeout(handle);
  }, [addQuery, selectedCustomer]);

  // Debounced move-target customer search
  useEffect(() => {
    if (!moveTarget) return undefined;
    const term = moveQuery.trim();
    if (!term) {
      setMoveResults([]);
      setMoveSearching(false);
      return undefined;
    }
    setMoveSearching(true);
    const handle = setTimeout(async () => {
      try {
        const data = await adminService.getCustomers({ search: term, limit: PAGE_SIZE, page: 1 });
        setMoveResults(data.customers || []);
      } catch (error) {
        console.error('Error searching customers:', error);
        setAlert({ type: 'error', message: error.message || 'Failed to search customers.' });
      } finally {
        setMoveSearching(false);
      }
    }, 300);
    return () => clearTimeout(handle);
  }, [moveQuery, moveTarget]);

  const handleSelectCustomer = async (customer) => {
    setSelectedCustomer(customer);
    setCustomerQuery('');
    setCustomerResults([]);
    setAddQuery('');
    setAddResults([]);
    setAlert(null);
    await refreshPlacementViews(customer.id);
  };

  const handleChangeCustomer = () => {
    setSelectedCustomer(null);
    setCurrentEmployees([]);
    setAddQuery('');
    setAddResults([]);
  };

  const handleAddEmployee = async (employee) => {
    if (!selectedCustomer) return;
    setBusyId(employee.id);
    try {
      await adminService.addEmployeeToCustomer(selectedCustomer.id, employee.id);
      setAlert({
        type: 'success',
        message: `Placement started for ${fullName(employee)} at ${selectedCustomer.name}.`
      });
      await refreshPlacementViews(selectedCustomer.id);
      // Drop the added employee from the search results so the list stays in sync.
      setAddResults((prev) => prev.filter((e) => e.id !== employee.id));
    } catch (error) {
      // One-customer rule (feedback #16): the backend answers 409 when the
      // employee already works for another customer — offer a move instead.
      const conflict = error.code === 'EMPLOYEE_ALREADY_ASSIGNED'
        && error.details && Array.isArray(error.details.conflicts)
        ? error.details.conflicts[0]
        : null;
      if (conflict) {
        const confirmed = window.confirm(
          `«${conflict.employeeName}» has a current placement at ${conflict.currentCustomerName}. ` +
          'An employee can only have one current placement. ' +
          `Reassign them to ${selectedCustomer.name}? The current placement will be ended and retained in history.`
        );
        if (confirmed) {
          try {
            await adminService.moveEmployee(employee.id, {
              fromCustomerId: conflict.currentCustomerId,
              toCustomerId: selectedCustomer.id
            });
            setAlert({
              type: 'success',
              message: `${fullName(employee)} was reassigned from ${conflict.currentCustomerName} to ${selectedCustomer.name}; the former placement remains in history.`
            });
            await refreshPlacementViews(selectedCustomer.id);
            setAddResults((prev) => prev.filter((e) => e.id !== employee.id));
          } catch (moveError) {
            console.error('Error moving employee:', moveError);
            setAlert({ type: 'error', message: moveError.message || 'Failed to move employee.' });
          }
        }
      } else {
        console.error('Error adding employee:', error);
        setAlert({ type: 'error', message: error.message || 'Failed to add employee.' });
      }
    } finally {
      setBusyId(null);
    }
  };

  const handleRemoveEmployee = async (employee) => {
    if (!selectedCustomer) return;
    if (!window.confirm(
      `End the current placement for ${fullName(employee)} at ${selectedCustomer.name}? `
      + 'This does not delete the placement; it remains in history.'
    )) return;
    setBusyId(employee.id);
    try {
      await adminService.removeEmployeeFromCustomer(selectedCustomer.id, employee.id);
      setAlert({
        type: 'success',
        message: `Placement ended for ${fullName(employee)} at ${selectedCustomer.name}; history was retained.`
      });
      await refreshPlacementViews(selectedCustomer.id);
    } catch (error) {
      console.error('Error removing employee:', error);
      setAlert({ type: 'error', message: error.message || 'Failed to remove employee.' });
    } finally {
      setBusyId(null);
    }
  };

  const openMove = (employee) => {
    setMoveTarget(employee);
    setMoveQuery('');
    setMoveResults([]);
  };

  const closeMove = () => {
    setMoveTarget(null);
    setMoveQuery('');
    setMoveResults([]);
    setMoveSubmitting(false);
  };

  const handleConfirmMove = async (targetCustomer) => {
    if (!moveTarget || !selectedCustomer) return;
    if (!window.confirm(
      `Reassign ${fullName(moveTarget)} from ${selectedCustomer.name} to ${targetCustomer.name}? `
      + 'The current placement will be ended and a new placement will start; both remain in placement history.'
    )) return;
    setMoveSubmitting(true);
    try {
      await adminService.moveEmployee(moveTarget.id, {
        fromCustomerId: selectedCustomer.id,
        toCustomerId: targetCustomer.id
      });
      setAlert({
        type: 'success',
        message: `${fullName(moveTarget)} was reassigned from ${selectedCustomer.name} to ${targetCustomer.name}.`
      });
      closeMove();
      await refreshPlacementViews(selectedCustomer.id);
    } catch (error) {
      console.error('Error moving employee:', error);
      setAlert({ type: 'error', message: error.message || 'Failed to move employee.' });
      setMoveSubmitting(false);
    }
  };

  // Ids already assigned to the selected customer — used to filter add-search results.
  const assignedIds = new Set(currentEmployees.map((e) => e.id));
  const addCandidates = addResults.filter((e) => !assignedIds.has(e.id));

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>Placement Manager</PageTitle>
      </PageHeader>

      {alert && (
        <AlertMessage type={alert.type}>
          {alert.message}
        </AlertMessage>
      )}

      {/* Step 1 — pick a customer */}
      <Card padding="md">
        <SectionTitle><FiSearch /> Select a customer</SectionTitle>
        <HelperText>
          Search by company name or contact email, then pick a customer to manage current and historical placements.
        </HelperText>
        <SearchBox>
          <SearchIcon><FiSearch /></SearchIcon>
          <SearchInput
            type="text"
            placeholder="Search customers..."
            value={customerQuery}
            onChange={(e) => setCustomerQuery(e.target.value)}
          />
        </SearchBox>

        {customerQuery.trim() && (
          <ResultList>
            {customerSearching ? (
              <MutedNote style={{ padding: '12px' }}>Searching...</MutedNote>
            ) : customerResults.length > 0 ? (
              customerResults.map((customer) => (
                <ClickableItem
                  key={customer.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleSelectCustomer(customer)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleSelectCustomer(customer);
                    }
                  }}
                >
                  <Person>
                    <PersonName>{customer.name}</PersonName>
                    <PersonMeta>{customer.contactEmail || '—'}</PersonMeta>
                  </Person>
                  <FiChevronRight />
                </ClickableItem>
              ))
            ) : (
              <MutedNote style={{ padding: '12px' }}>No customers found.</MutedNote>
            )}
          </ResultList>
        )}
      </Card>

      {/* Step 2 — manage the selected customer's team */}
      {selectedCustomer && (
        <div style={{ marginTop: '16px' }}>
          <SelectedBar>
            <SelectedInfo>
              <SelectedLabel>Managing team for</SelectedLabel>
              <SelectedName>{selectedCustomer.name}</SelectedName>
            </SelectedInfo>
            <Button variant="outline" size="sm" onClick={handleChangeCustomer}>
              Change customer
            </Button>
          </SelectedBar>

          {localActions ? (
            <SemanticsNote>
              A reassignment ends the employee’s current placement and starts a new one at the destination customer.
              Ending or reassigning never deletes placement history.
            </SemanticsNote>
          ) : (
            <SemanticsNote>
              Placements and employee status are managed in CRM. This page shows current and historical placements only;
              start, end or reassign a placement in CRM and it is delivered to Tempus automatically.
            </SemanticsNote>
          )}

          <Grid>
            {/* Current employees */}
            <Card padding="md">
              <SectionTitle>
                <FiUsers /> Current placements{' '}
                <CountBadge>{currentEmployees.length}</CountBadge>
              </SectionTitle>
              {currentLoading ? (
                <EmptyState>Loading assigned employees...</EmptyState>
              ) : currentEmployees.length > 0 ? (
                <ResultList>
                  {currentEmployees.map((employee) => {
                    const placement = currentPlacementAtCustomer(employee, selectedCustomer.id);
                    return (
                    <ResultItem key={employee.id}>
                      <Person>
                        <PersonName>
                          {fullName(employee)}
                          {!employee.isActive && isCrmSynced(employee) && <SyncBadge>CRM synced · inactive</SyncBadge>}
                        </PersonName>
                        <PersonMeta>
                          {employeeMeta(employee)}
                          {placement ? `${employeeMeta(employee) ? ' · ' : ''}${formatPlacementDate(placement.startDate)} – ${formatPlacementDate(placement.endDate)}` : ''}
                        </PersonMeta>
                      </Person>
                      {localActions ? (
                        <RowActions>
                          <SmallButton
                            variant="outline"
                            onClick={() => openMove(employee)}
                            disabled={busyId === employee.id}
                            title="End this placement and start one at another customer"
                          >
                            <FiRepeat /> Reassign
                          </SmallButton>
                          <DangerButton
                            onClick={() => handleRemoveEmployee(employee)}
                            disabled={busyId === employee.id}
                            title="End this placement and retain it in history"
                          >
                            <FiUserX /> End placement
                          </DangerButton>
                        </RowActions>
                      ) : (
                        <RowActions>
                          <ManageInCrm href={crmEmployeeHref(policy, employee)} />
                        </RowActions>
                      )}
                    </ResultItem>
                    );
                  })}
                </ResultList>
              ) : (
                <EmptyState>No current employee placements at this customer.</EmptyState>
              )}
            </Card>

            {/* Add employees */}
            {!localActions ? (
              <Card padding="md">
                <SectionTitle><FiUserPlus /> Start a placement</SectionTitle>
                <HelperText>
                  New placements are started in CRM by placing the employee with this customer.
                </HelperText>
                <ManageInCrm href={crmEmployeesHref(policy)} />
              </Card>
            ) : (
            <Card padding="md">
              <SectionTitle><FiUserPlus /> Start a placement</SectionTitle>
              <HelperText>
                Search all employees by name or email and start a placement at this customer.
              </HelperText>
              <SearchBox>
                <SearchIcon><FiSearch /></SearchIcon>
                <SearchInput
                  type="text"
                    placeholder="Search employees for a placement..."
                  value={addQuery}
                  onChange={(e) => setAddQuery(e.target.value)}
                />
              </SearchBox>

              {addQuery.trim() && (
                <ResultList>
                  {addSearching ? (
                    <MutedNote style={{ padding: '12px' }}>Searching...</MutedNote>
                  ) : addCandidates.length > 0 ? (
                    addCandidates.map((employee) => (
                      <ResultItem key={employee.id}>
                        <Person>
                          <PersonName>
                            {fullName(employee)}
                            {!employee.isActive && isCrmSynced(employee) && <SyncBadge>CRM synced · inactive</SyncBadge>}
                          </PersonName>
                          <PersonMeta>{employeeMeta(employee)}</PersonMeta>
                        </Person>
                        <SmallButton
                          onClick={() => handleAddEmployee(employee)}
                          disabled={busyId === employee.id}
                          title="Start a placement at this customer"
                        >
                          <FiUserPlus /> Start
                        </SmallButton>
                      </ResultItem>
                    ))
                  ) : (
                    <MutedNote style={{ padding: '12px' }}>
                      No matching employees (or all matches already have this current placement).
                    </MutedNote>
                  )}
                </ResultList>
              )}
            </Card>
            )}
          </Grid>

          <Card padding="md" style={{ marginTop: '16px' }}>
            <SectionTitle><FiUsers /> Placement history</SectionTitle>
            <PlacementHistory
              placements={getPlacements(selectedCustomer)}
              employees={currentEmployees}
              perspective="customer"
              emptyMessage="No historical placement records are available for this customer yet."
            />
          </Card>
        </div>
      )}

      {/* Move employee modal */}
      {localActions && moveTarget && selectedCustomer && (
        <Modal
          title={`Reassign ${fullName(moveTarget)}`}
          onClose={closeMove}
        >
          <HelperText>
            Current placement: <strong>{selectedCustomer.name}</strong>. Choose the destination customer.
            Confirming ends the current placement and starts a new one; the previous period remains in history.
          </HelperText>
          <SearchBox>
            <SearchIcon><FiSearch /></SearchIcon>
            <SearchInput
              type="text"
              placeholder="Search destination customer..."
              value={moveQuery}
              onChange={(e) => setMoveQuery(e.target.value)}
              autoFocus
            />
          </SearchBox>

          {moveQuery.trim() && (
            <ResultList>
              {moveSearching ? (
                <MutedNote style={{ padding: '12px' }}>Searching...</MutedNote>
              ) : (
                (() => {
                  const targets = moveResults.filter((c) => c.id !== selectedCustomer.id);
                  if (targets.length === 0) {
                    return <MutedNote style={{ padding: '12px' }}>No other customers found.</MutedNote>;
                  }
                  return targets.map((customer) => (
                    <ResultItem key={customer.id}>
                      <Person>
                        <PersonName>{customer.name}</PersonName>
                        <PersonMeta>{customer.contactEmail || '—'}</PersonMeta>
                      </Person>
                      <SmallButton
                        onClick={() => handleConfirmMove(customer)}
                        disabled={moveSubmitting}
                        title="Reassign to this customer"
                      >
                        <FiRepeat /> Reassign here
                      </SmallButton>
                    </ResultItem>
                  ));
                })()
              )}
            </ResultList>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '24px' }}>
            <Button variant="outline" onClick={closeMove} disabled={moveSubmitting}>
              Cancel
            </Button>
          </div>
        </Modal>
      )}
    </PageContainer>
  );
};

export default Assignments;
